import React, { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMountEffect } from '../../hooks/useMountEffect'
import { ArrowRight, ArrowUpRight, Play, X } from 'lucide-react'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../UI/Modal/ModalPrimitive'
import { FbCheckbox } from '../UI/FbCheckbox'
import { NoteManager } from '../Notes/NoteManager'
import { StagePill } from '../FbUI/StagePill'
import { TextPill } from '../FbUI/TextPill'
import { StageColorProvider } from '../SalesStage/StageColorContext'
import {
  DEFAULT_STAGE_COLOR,
  getPipelineColorForStage,
  sortStagesPipelineOrder,
  isTerminalStage,
} from '../SalesStage/stageColors'
import { colors } from '../../styles/tokens'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import ReactSelectWrapper from '../FormUtils/ReactSelectWrapper'
import useApiService from '../../services/api'
import type { SingleValue } from 'react-select'
import type {
  EnrichedAction,
  PreparedAction,
  AddNoteAction,
  ActionClickStatus,
  ActionClickUpdateStatus,
} from '../../services/recommendations_contracts'
import { SIGNAL_CONFIG } from './recommendationConfig'
import {
  URGENCY_CIRCLE_STYLES,
  URGENCY_ICONS,
  URGENCY_TOOLTIPS,
  SIGNAL_TOOLTIPS,
} from './constants'

interface EnrichedActionCardProps {
  action: EnrichedAction
  /** Optional callback when the card is clicked (for tracking) */
  onCardClick?: () => void
  /** Optional callback when user clicks Dismiss */
  onStatusClick?: (status: ActionClickUpdateStatus) => void
  /** Current action status (for visual treatment) */
  actionStatus?: ActionClickStatus | null
  /** Whether the action was auto-completed by the system */
  actionAutoCompleted?: boolean
  /** Optional callback when user confirms prepared actions */
  onApplyPreparedActions?: (preparedActions: PreparedAction[]) => void
  /** When true, Apply/Dismiss buttons are disabled (viewing another user's recs) */
  readOnly?: boolean
}

const DISABLED_TOOLTIP: Record<
  'stale' | 'finalized' | 'readonly',
  { title: string; content: string }
> = {
  stale: {
    title: 'Outdated',
    content:
      'The deal stage has already changed — this action is no longer applicable.',
  },
  finalized: {
    title: 'Already actioned',
    content:
      'This recommendation has already been marked as done or dismissed.',
  },
  readonly: {
    title: 'Read-only',
    content: 'You can only apply actions on your own recommendations.',
  },
}

// ---------------------------------------------------------------------------
// Multi-Action Confirmation Dialog
// ---------------------------------------------------------------------------

interface NoteFormValues {
  note: string
  attachments: File[]
}

/** Convert plain-text paragraphs (separated by blank lines) into Quill-compatible HTML. */
function plainTextToHtml(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return text
    .split('\n\n')
    .map((p) => `<p>${escape(p).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/** Entity summary line showing company/deal/contact context. */
const EntitySummary: React.FC<{ action: EnrichedAction }> = ({ action }) => {
  const isDeal = SIGNAL_CONFIG[action.signal_type]?.linkTarget === 'deal'
  return (
    <div className="flex items-center gap-1.5 text-xs text-fg-secondary">
      {isDeal && action.deal_name && (
        <>
          <span className="font-medium text-fg">{action.deal_name}</span>
          <span className="text-line">|</span>
        </>
      )}
      <span className="text-fb-dark-green font-medium">
        {action.company_name}
      </span>
      {action.company_subcategory && (
        <>
          <span className="text-line">·</span>
          <span className="text-fg-muted">{action.company_subcategory}</span>
        </>
      )}
      {action.contact_name && (
        <>
          <span className="text-line">|</span>
          <span>{action.contact_name}</span>
        </>
      )}
    </div>
  )
}

interface MultiActionConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  preparedActions: PreparedAction[]
  action: EnrichedAction
  onConfirm: (selectedActions: PreparedAction[]) => void
}

/**
 * Confirmation dialog showing all prepared actions as a checklist.
 * Users can toggle individual actions on/off and edit the draft note
 * before confirming. Confirming with zero actions resolves as a no-op.
 */
const MultiActionConfirmDialog: React.FC<MultiActionConfirmDialogProps> = ({
  open,
  onOpenChange,
  preparedActions,
  action,
  onConfirm,
}) => {
  const [enabled, setEnabled] = useState<boolean[]>(() =>
    preparedActions.map((pa) => !pa.stale)
  )

  const stageAction = preparedActions.find((pa) => pa.type === 'update_stage')
  const [selectedStageId, setSelectedStageId] = useState<number | null>(
    stageAction?.type === 'update_stage' ? stageAction.target_stage_id : null
  )

  const api = useApiService()
  const { data: stages } = api.useGetSalesStages()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const pipelineStageOptions = useMemo(() => {
    if (!stages) return []
    const sorted = sortStagesPipelineOrder(stages, customOrderEnabled)
    const regular = sorted.filter((s) => !isTerminalStage(s))
    const terminal = sorted.filter((s) => isTerminalStage(s))
    return [...regular, ...terminal].map((s) => {
      const pipelineColor = getPipelineColorForStage(
        s.id,
        stages,
        customOrderEnabled
      )
      return {
        label: s.stage,
        value: s.id,
        bg: pipelineColor?.bg ?? DEFAULT_STAGE_COLOR,
        text: pipelineColor?.text ?? colors['fg'],
      }
    })
  }, [stages, customOrderEnabled])

  const selectedStageOption = useMemo(
    () => pipelineStageOptions.find((o) => o.value === selectedStageId) ?? null,
    [pipelineStageOptions, selectedStageId]
  )

  const noteAction = preparedActions.find(
    (pa): pa is AddNoteAction => pa.type === 'add_note'
  )

  const { control, getValues } = useForm<NoteFormValues>({
    defaultValues: {
      note: noteAction ? plainTextToHtml(noteAction.draft_content) : '',
      attachments: [],
    },
  })

  const toggleAction = (index: number) => {
    setEnabled((prev) => {
      const next = [...prev]
      next[index] = !next[index]
      return next
    })
  }

  const enabledCount = enabled.filter(Boolean).length

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation()
    const selected = preparedActions
      .filter((_, i) => enabled[i])
      .map((pa) => {
        if (pa.type === 'update_stage' && selectedStageId) {
          const stageName =
            pipelineStageOptions.find((o) => o.value === selectedStageId)
              ?.label ?? pa.target_stage_name
          return {
            ...pa,
            target_stage_id: selectedStageId,
            target_stage_name: stageName,
          }
        }
        if (pa.type === 'add_note') {
          const values = getValues()
          return {
            ...pa,
            draft_content: values.note,
            attachments: values.attachments,
          }
        }
        return pa
      })
    onOpenChange(false)
    onConfirm(selected)
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation()
    onOpenChange(false)
  }

  const actionTitle = (pa: PreparedAction): string => {
    if (pa.type === 'update_stage') return 'Update deal stage'
    if (pa.type === 'add_note') return 'Add deal note'
    return (pa as PreparedAction).type
  }

  const actionDetail = (pa: PreparedAction, index: number): React.ReactNode => {
    if (!enabled[index]) return null
    if (pa.type === 'update_stage') {
      return (
        <StageColorProvider value="pipeline">
          <div className="flex items-center gap-2 mt-1.5">
            <StagePill stageId={pa.current_stage_id} />
            <ArrowRight size={14} className="text-fg-muted flex-shrink-0" />
            <ReactSelectWrapper
              className="min-w-[160px]"
              options={pipelineStageOptions}
              value={selectedStageOption}
              onChange={(option: unknown) => {
                const typed = option as SingleValue<{
                  label: string
                  value: number
                }>
                if (typed) setSelectedStageId(typed.value)
              }}
              formatOptionLabel={(data: unknown) => {
                const opt = data as {
                  label: string
                  bg?: string
                  text?: string
                }
                return (
                  <TextPill
                    backgroundColor={opt.bg ?? DEFAULT_STAGE_COLOR}
                    textColor={opt.text ?? colors['fg']}
                  >
                    {opt.label}
                  </TextPill>
                )
              }}
              isSearchable={false}
              menuPortalTarget={document.body}
            />
          </div>
        </StageColorProvider>
      )
    }
    if (pa.type === 'add_note') {
      return (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <NoteManager
            control={control}
            noteName="note"
            attachmentsName="attachments"
            placeholder="Edit note before applying..."
            enableMentions
            editorClassName="h-28 bg-surface-alt"
            toolbarClassName="bg-surface-alt"
            label=""
          />
        </div>
      )
    }
    return null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            Apply Actions to{' '}
            {SIGNAL_CONFIG[action.signal_type]?.linkTarget === 'deal'
              ? 'Deal'
              : 'Company'}
          </DialogTitle>
          <EntitySummary action={action} />
        </DialogHeader>
        <div className="grid grid-cols-[16px_1fr] gap-x-3">
          {preparedActions.map((pa, index) => (
            <React.Fragment key={`${pa.type}-${index}`}>
              {/* Checkbox — centered in 16px column */}
              <div
                className={cn(
                  'flex justify-center items-center h-5',
                  !enabled[index] && 'opacity-60'
                )}
              >
                <FbCheckbox
                  checked={enabled[index]}
                  onChange={() => toggleAction(index)}
                />
              </div>
              {/* Content */}
              <div className={cn('min-w-0', !enabled[index] && 'opacity-60')}>
                <span className="text-sm font-medium text-fg leading-5 block h-5">
                  {actionTitle(pa)}
                </span>
                {actionDetail(pa, index)}
              </div>
              {/* Connector line */}
              {index < preparedActions.length - 1 && (
                <>
                  <div className="flex justify-center py-1">
                    <div className="w-px h-full bg-line" />
                  </div>
                  <div />
                </>
              )}
            </React.Fragment>
          ))}
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-sm rounded border border-line text-fg-secondary hover:bg-surface-alt transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 text-sm rounded bg-fb-dark-green text-white hover:bg-fb-dark-green-hover transition-colors font-medium"
          >
            {enabledCount > 0
              ? `Apply (${enabledCount})`
              : 'Resolve (No Action)'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Resolved Badge
// ---------------------------------------------------------------------------

const GREEN_BADGE = 'bg-fb-green-50 text-fb-dark-green border-fb-green-100'
const GREY_BADGE = 'bg-surface-alt text-fg-secondary border-line-subtle'

function getResolvedBadge(
  status: ActionClickStatus | null,
  autoCompleted: boolean
): { label: string; className: string } | null {
  if (status === 'done') {
    return {
      label: autoCompleted ? 'Manually Resolved' : 'Applied',
      className: GREEN_BADGE,
    }
  }
  if (status === 'dismissed') {
    return {
      label: autoCompleted ? 'Manually Resolved' : 'Dismissed',
      className: autoCompleted ? GREEN_BADGE : GREY_BADGE,
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// EnrichedActionCard
// ---------------------------------------------------------------------------

/**
 * Compact action card with risk indicator and company link.
 *
 * Features:
 * - Colored icon circle driven by urgency (red=do_now, amber=today, green=this_week)
 * - Black border, no left stripe
 * - Dismiss (7-day suppression) and Apply (multi-action dialog) in footer
 * - Link to deal (email_stage_change) or company (all others)
 * - Context snippets from last interaction
 */
export const EnrichedActionCard: React.FC<EnrichedActionCardProps> = ({
  action,
  onCardClick,
  onStatusClick,
  actionStatus = null,
  actionAutoCompleted = false,
  onApplyPreparedActions,
  readOnly = false,
}) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const [applyDialogOpen, setApplyDialogOpen] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)
  const { iconBg, iconColor } = URGENCY_CIRCLE_STYLES[action.urgency]
  const UrgencyIcon = URGENCY_ICONS[action.urgency]
  const signalTypeLabel = SIGNAL_TOOLTIPS[action.signal_type].title

  const isStatusFinalized =
    actionStatus === 'done' || actionStatus === 'dismissed'
  const resolvedBadge = getResolvedBadge(actionStatus, actionAutoCompleted)

  const preparedActions = useMemo(
    () => action.prepared_actions ?? [],
    [action.prepared_actions]
  )
  const allActionsStale = preparedActions.every((pa) => pa.stale)
  const applyDisabled = readOnly || isStatusFinalized || allActionsStale

  const applyDisabledReason: 'readonly' | 'finalized' | 'stale' | undefined =
    readOnly
      ? 'readonly'
      : isStatusFinalized
        ? 'finalized'
        : allActionsStale
          ? 'stale'
          : undefined

  // email_stage_change actions link to the deal; all others link to the company
  const destination =
    action.signal_type === 'email_stage_change' && action.deal_id
      ? `/deals/${action.deal_id}/`
      : action.company_link
  const viewLabel =
    action.signal_type === 'email_stage_change' ? 'View deal' : 'View company'

  const handleCardClick = () => {
    setIsExpanded((prev) => !prev)
  }

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onCardClick) onCardClick()
    navigate(destination)
  }

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded((prev) => !prev)
  }

  // Detect if text is actually truncated by line-clamp (measured once on mount
  // while collapsed; text content is stable for the lifetime of a given card).
  useMountEffect(() => {
    const el = textRef.current
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight)
    }
  })

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        'rounded-lg border border-fg bg-surface p-3 cursor-default',
        actionStatus === 'done' && 'opacity-50',
        actionStatus === 'dismissed' && 'opacity-40'
      )}
    >
      <div className="flex items-start gap-2">
        {/* Risk Icon with Tooltip */}
        <Tooltip
          title={URGENCY_TOOLTIPS[action.urgency].title}
          content={URGENCY_TOOLTIPS[action.urgency].content}
          placement="top"
          delay={[500, 0]}
        >
          <div
            className={cn(
              'flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full',
              iconBg,
              iconColor
            )}
          >
            <UrgencyIcon size={16} />
          </div>
        </Tooltip>

        <div className="flex-1 min-w-0">
          {/* Header: Signal type label + company + action task */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-fg text-sm leading-tight">
                {signalTypeLabel}
              </h4>
              <div className="mt-0.5">
                <EntitySummary action={action} />
              </div>
            </div>
            {resolvedBadge && (
              <span
                className={cn(
                  'ml-2 flex-shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-xxs font-medium',
                  resolvedBadge.className
                )}
              >
                {resolvedBadge.label}
              </span>
            )}
          </div>

          {/* Action task — specific what-to-do */}
          <p className="text-sm text-fg mt-1">{action.action}</p>

          {/* Recommendation - expandable */}
          <p
            ref={textRef}
            onClick={handleExpandClick}
            className={cn(
              'text-sm text-fg-secondary mt-1',
              !isExpanded && 'line-clamp-4'
            )}
          >
            {action.recommendation}
          </p>
          {!isExpanded && isTruncated && (
            <button
              type="button"
              onClick={handleExpandClick}
              className="text-xs text-fb-dark-green hover:text-fb-dark-green-hover font-medium mt-0.5"
            >
              Show more
            </button>
          )}

          {/* Context Snippet - only if exists and compact */}
          {action.context_snippet && (
            <p className="text-xs text-fg-muted italic mt-1 line-clamp-1">
              &ldquo;{action.context_snippet}&rdquo;
            </p>
          )}

          {/* Action footer: View link (left) + Apply + Dismiss (right) */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-line-subtle">
            <button
              type="button"
              onClick={handleNavigate}
              className="inline-flex items-center gap-1 text-xs text-fb-dark-green hover:text-fb-dark-green-hover font-medium transition-colors"
            >
              {viewLabel}
              <ArrowUpRight size={12} />
            </button>
            <div className="flex items-center gap-1">
              {onApplyPreparedActions && preparedActions.length > 0 && (
                <>
                  {applyDisabled ? (
                    <Tooltip
                      title={
                        DISABLED_TOOLTIP[applyDisabledReason ?? 'stale'].title
                      }
                      content={
                        DISABLED_TOOLTIP[applyDisabledReason ?? 'stale'].content
                      }
                      placement="top"
                      delay={[200, 0]}
                    >
                      <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded opacity-40 cursor-not-allowed bg-fb-dark-green text-white"
                      >
                        <Play size={12} fill="currentColor" />
                        <span>Apply</span>
                      </button>
                    </Tooltip>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setApplyDialogOpen(true)
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors bg-fb-dark-green text-white hover:bg-fb-dark-green-hover font-medium"
                    >
                      <Play size={12} fill="currentColor" />
                      <span>Apply</span>
                    </button>
                  )}
                  {/* Render only when open so state resets each time */}
                  {applyDialogOpen && (
                    <MultiActionConfirmDialog
                      open={applyDialogOpen}
                      onOpenChange={setApplyDialogOpen}
                      preparedActions={preparedActions}
                      action={action}
                      onConfirm={onApplyPreparedActions}
                    />
                  )}
                </>
              )}
              {!isStatusFinalized && (
                <Tooltip
                  title="Dismiss"
                  content={
                    readOnly
                      ? 'You can only dismiss your own recommendations.'
                      : 'Hide this recommendation for 7 days.'
                  }
                  placement="top"
                  delay={[300, 0]}
                >
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={(e) => {
                      e.stopPropagation()
                      onStatusClick?.('dismissed')
                    }}
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors',
                      readOnly
                        ? 'opacity-40 cursor-not-allowed text-fg-secondary'
                        : 'text-fg-secondary hover:text-fg hover:bg-surface-alt'
                    )}
                    aria-label="Dismiss"
                  >
                    <X size={14} />
                    <span>Dismiss</span>
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
